import { useEffect, useMemo, useRef } from "react";
import { useMap } from "@vis.gl/react-google-maps";
import type { Task } from "@/api.types.v2";
import { useTasks, filterTasksByBounds, taskSearchQueries } from "@/api/tasks";
import { useShortcutsStore } from "@/screens/dashboard/shortcuts.store";
import { applyTaskScopeFilter } from "@/features/dashboard/task-urgency";
import { useNow } from "@/features/dashboard/use-now";
import { useDispatchDateStore } from "@/features/dashboard/dispatch-date.store";
import { useCarrierScope } from "@/contexts/carrier-scope-context";

export interface TasksSelectorProps {
  onSelectionComplete: (
    bounds: google.maps.LatLngBounds,
    tasks: Task[]
  ) => void;
}

/**
 * v2 box-select. Same selection logic as the shared
 * `features/tasks/tasks-selector.tsx`, but the marquee is drawn as a plain DOM
 * `<div>` over the map container instead of a `google.maps.Rectangle`.
 *
 * Why: a Maps Rectangle is a *vector overlay* — every `setBounds` re-rasterises
 * the whole overlay pane (route polylines + every AdvancedMarker), so the box
 * trails the cursor on a busy board no matter how the redraw is throttled. A CSS
 * div positioned from raw pixel coords costs nothing the map has to repaint; the
 * geographic bounds are taken from the start/end `latLng` of Maps' own
 * mousedown/up, so selection accuracy is unchanged. Live `/dispatch` keeps its
 * own Rectangle-based copy untouched.
 */
export function TasksSelector({ onSelectionComplete }: TasksSelectorProps) {
  const {
    showPickups,
    showDeliveries,
    showCarrierDropoffs,
    showReturns,
    tripFilter,
    windowFilter,
  } = useShortcutsStore();
  const now = useNow(60_000);
  const selectedDate = useDispatchDateStore((s) => s.selectedDate);
  const map = useMap();

  // Scope the task queries by the active carrier / dispatch-center selection so
  // box-select matches what the cluster markers actually show — without this the
  // selector pulls in out-of-scope tasks that aren't even rendered on the map.
  const { carrierIds, dispatchCenterIds } = useCarrierScope();
  const scope = {
    carrierIds: carrierIds.join(","),
    dispatchCenterIds: dispatchCenterIds.join(","),
  };

  const { data: pickupsResponse } = useTasks({
    ...taskSearchQueries.activePickupTasks,
    ...scope,
  });
  const { data: deliveriesResponse } = useTasks({
    ...taskSearchQueries.activeDeliveryTasks,
    ...scope,
  });
  const { data: carrierDropoffsResponse } = useTasks({
    ...taskSearchQueries.activeCarrierDropoffTasks,
    ...scope,
  });
  const { data: returnsResponse } = useTasks({
    ...taskSearchQueries.activeReturnTasks,
    ...scope,
  });
  const { data: tripsResponse } = useTasks({
    ...taskSearchQueries.activeTasks,
    ...scope,
  });

  const tasks = useMemo(() => {
    const taskMap = new Map<string, Task>();
    const addTasks = (list: Task[]) => {
      list.forEach((task) => taskMap.set(task.id, task));
    };

    if (showPickups && pickupsResponse?.data) addTasks(pickupsResponse.data);
    if (showDeliveries && deliveriesResponse?.data)
      addTasks(deliveriesResponse.data);
    if (showCarrierDropoffs && carrierDropoffsResponse?.data)
      addTasks(carrierDropoffsResponse.data);
    if (showReturns && returnsResponse?.data) addTasks(returnsResponse.data);

    if (
      (tripFilter.showAll || tripFilter.visibleTripIds.length > 0) &&
      tripsResponse?.data
    ) {
      const tasksWithTrips = tripsResponse.data.filter((task) => {
        if (task.tripId) {
          if (tripFilter.showAll) {
            return !tripFilter.hiddenTripIds.includes(task.tripId);
          }
          return tripFilter.visibleTripIds.includes(task.tripId);
        }
        return false;
      });
      addTasks(tasksWithTrips);
    }

    return applyTaskScopeFilter(
      Array.from(taskMap.values()),
      selectedDate,
      windowFilter,
      now
    );
  }, [
    pickupsResponse?.data,
    deliveriesResponse?.data,
    carrierDropoffsResponse?.data,
    returnsResponse?.data,
    tripsResponse?.data,
    showPickups,
    showDeliveries,
    showCarrierDropoffs,
    showReturns,
    tripFilter,
    selectedDate,
    windowFilter,
    now,
  ]);

  // Latest data/callback for mouseup, without re-binding the map listeners.
  const selectionRef = useRef({ tasks, onSelectionComplete, filters: {} });
  selectionRef.current = {
    tasks,
    onSelectionComplete,
    filters: {
      showPickups,
      showDeliveries,
      showCarrierDropoffs,
      showReturns,
      showTrips: tripFilter.showAll || tripFilter.visibleTripIds.length > 0,
    },
  };

  useEffect(() => {
    if (!map) return;
    const container = map.getDiv();
    if (!container) return;

    const prevCursor = map.get("draggableCursor") as string | null | undefined;
    map.setOptions({ draggable: false, draggableCursor: "crosshair" });

    // DOM marquee — a CSS rectangle the map never has to repaint.
    const marquee = document.createElement("div");
    marquee.style.cssText =
      "position:absolute;border:2px solid #0066ff;background:rgba(0,102,255,0.15);" +
      "pointer-events:none;z-index:9999;display:none;will-change:left,top,width,height;";
    container.appendChild(marquee);

    let startLatLng: google.maps.LatLng | null = null;
    let startX = 0;
    let startY = 0;
    let curX = 0;
    let curY = 0;
    let dragging = false;
    let frame: number | null = null;

    const draw = () => {
      frame = null;
      const x = Math.min(startX, curX);
      const y = Math.min(startY, curY);
      marquee.style.left = `${x}px`;
      marquee.style.top = `${y}px`;
      marquee.style.width = `${Math.abs(curX - startX)}px`;
      marquee.style.height = `${Math.abs(curY - startY)}px`;
    };

    const onMove = (ev: MouseEvent) => {
      if (!dragging) return;
      const rect = container.getBoundingClientRect();
      curX = ev.clientX - rect.left;
      curY = ev.clientY - rect.top;
      frame ??= requestAnimationFrame(draw);
    };

    const stopVisual = () => {
      dragging = false;
      window.removeEventListener("mousemove", onMove);
      if (frame !== null) {
        cancelAnimationFrame(frame);
        frame = null;
      }
      marquee.style.display = "none";
    };

    const downListener = map.addListener(
      "mousedown",
      (e: google.maps.MapMouseEvent) => {
        const dom = e.domEvent as MouseEvent | undefined;
        if (!e.latLng || !dom) return;
        const rect = container.getBoundingClientRect();
        startLatLng = e.latLng;
        startX = curX = dom.clientX - rect.left;
        startY = curY = dom.clientY - rect.top;
        dragging = true;
        marquee.style.display = "block";
        draw();
        window.addEventListener("mousemove", onMove);
      }
    );

    const upListener = map.addListener(
      "mouseup",
      (e: google.maps.MapMouseEvent) => {
        if (!startLatLng) return;
        const moved =
          Math.abs(curX - startX) > 4 || Math.abs(curY - startY) > 4;
        const end = e.latLng;
        stopVisual();
        if (end && moved) {
          const bounds = new google.maps.LatLngBounds();
          bounds.extend(startLatLng);
          bounds.extend(end);
          const {
            tasks: t,
            filters,
            onSelectionComplete: cb,
          } = selectionRef.current;
          cb(bounds, filterTasksByBounds(t, bounds, filters));
        }
        startLatLng = null;
      }
    );

    // Fallback: if the pointer is released off the map, Maps' mouseup won't fire
    // — clean up the visual (no selection without an end latLng). Deferred so the
    // map's own mouseup (which runs the selection) always wins when both fire.
    const onWindowUp = () => {
      setTimeout(() => {
        if (dragging) {
          stopVisual();
          startLatLng = null;
        }
      }, 0);
    };
    window.addEventListener("mouseup", onWindowUp);

    return () => {
      stopVisual();
      window.removeEventListener("mouseup", onWindowUp);
      downListener.remove();
      upListener.remove();
      marquee.remove();
      map.setOptions({ draggable: true, draggableCursor: prevCursor ?? null });
    };
  }, [map]);

  return null;
}
